import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  Row,
  Col,
  message,
  Card,
  Segmented,
  InputNumber,
} from "antd";
import { ENDPOINTS } from "../../constants/endpoints.constant";
import AutoComplete from "react-google-autocomplete";
import { State, City } from "country-state-city";
import { EntityType } from "../kyc-verification/kyc/types/kyc.types";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/store";
import { useAPI } from "../../hooks/useAPI";
import { updateCall, getCall } from "../../constants/apicalls.constant";
import { logged } from "../../store/slices/user.slice";
import { User } from "../../models/user";
import { AddressType } from "../../models/onboarding";

const { Option } = Select;

const OrganisationDetail: React.FC = () => {
  const user = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [entityType, setEntityType] = useState<EntityType>(
    (user.type?.toLowerCase() as EntityType) || "organisation",
  );

  const { loading: profileLoading, trigger: triggerUpdateProfile } = useAPI<{
    status: boolean;
    message: string;
  }>(ENDPOINTS.update_profile);
  const { trigger: triggerMe } = useAPI<User>(ENDPOINTS.me);

  const address = user.address as any;

  useEffect(() => {
    form.setFieldsValue({
      organisation_name: user.legal_name || user.organization?.legal_name || "",
      address: address?.line1 || "",
      area: address?.line2 || "",
      state: address?.state || "",
      city: address?.city || "",
      pincode: address?.pincode?.toString() || "",
      country: "India",
    });
    if (user.type) {
      setEntityType(user.type.toLowerCase() as EntityType);
    }
  }, [user]);

  const allStates = State.getStatesOfCountry("IN");
  const selectedStateName = Form.useWatch("state", form);
  const selectedState = allStates.find((s) => s.name === selectedStateName);
  const cities = selectedState
    ? City.getCitiesOfState("IN", selectedState.isoCode)
    : [];

  const onPlaceSelected = (autocompleteOrPlace: any) => {
    const place =
      autocompleteOrPlace?.address_components ||
      autocompleteOrPlace?.getPlace?.()
        ? autocompleteOrPlace?.address_components
          ? autocompleteOrPlace
          : autocompleteOrPlace.getPlace()
        : null;

    if (!place || !place.address_components) return;

    const components = place.address_components;
    let addressParts: string[] = [];
    let city = "";
    let state = "";
    let country = "";
    let pincode = "";
    let area = "";

    components.forEach((component: any) => {
      const types = component.types;

      if (
        types.includes("street_number") ||
        types.includes("route") ||
        types.includes("sublocality_level_1") ||
        types.includes("sublocality_level_2") ||
        types.includes("neighborhood") ||
        types.includes("premise") ||
        types.includes("subpremise")
      ) {
        addressParts.push(component.long_name);
      }

      if (types.includes("locality")) {
        city = component.long_name;
      } else if (!city && types.includes("administrative_area_level_2")) {
        city = component.long_name;
      }

      if (types.includes("administrative_area_level_1")) {
        state = component.long_name;
      }
      if (types.includes("country")) {
        country = component.long_name;
      }
      if (types.includes("postal_code")) {
        pincode = component.long_name;
      }
      if (types.includes("sublocality") || types.includes("neighborhood")) {
        area = component.long_name;
      }
    });

    const fullAddress =
      addressParts.length > 0 ? addressParts.join(", ") : place.name || "";

    form.setFieldsValue({
      address: fullAddress || place.formatted_address,
      city,
      state,
      country,
      pincode,
      area: area || city,
    });
  };

  const onFinish = async (values: any) => {
    const payload: any = {
      entity_type: entityType,
      organization_name: values.organisation_name,
      address: {
        line1: values.address,
        line2: values.area || "",
        city: values.city,
        state: values.state,
        pincode: values.pincode,
      },
      status: "active",
    };

    const res = await triggerUpdateProfile(updateCall("", payload));
    if (res?.status) {
      message.success("Organization details updated successfully!");
      const userRes = await triggerMe(getCall<User>());
      if (userRes?.status && userRes?.data) {
        dispatch(
          logged({
            ...userRes.data,
            ...userRes.data.organization,
            id: userRes.data.id,
            is_verified: userRes.data.is_verified,
            organizer_id: userRes.data.organization?.id,
            address: userRes.data.organization?.address ?? ({} as AddressType),
            workspace: {
              id: userRes.data.workspace?.id,
              name: userRes.data.workspace?.name,
              feature_image: userRes.data.workspace?.feature_image,
              role: userRes.data.workspace?.role,
            },
            user_workspaces: userRes.data.user_workspaces,
            legal_name: userRes.data.organization?.legal_name,
            gst: userRes.data.organization?.gst_details ?? [],
          }),
        );
      }
    } else {
      message.error(res?.message || "Failed to update. Please try again.");
    }
  };

  return (
    <div className="h-full mb-10">
      <Card title="Organization Details" className="w-full shadow-md">
        <div className="mb-6">
          <p className="text-sm font-medium text-gray-700 mb-2">Account Type</p>
          <Segmented
            options={[
              { label: "Organisation", value: "organisation" },
              { label: "Individual", value: "individual" },
            ]}
            value={entityType}
            onChange={(val) => setEntityType(val as EntityType)}
            block
          />
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ country: "India" }}
        >
          <Form.Item
            label={
              entityType === "individual" ? "Full Name" : "Organisation Name"
            }
            name="organisation_name"
            rules={[
              {
                required: true,
                message:
                  entityType === "individual"
                    ? "Full name is required"
                    : "Organisation name is required",
              },
            ]}
          >
            <Input
              placeholder={
                entityType === "individual"
                  ? "Enter full name"
                  : "Enter organisation name"
              }
            />
          </Form.Item>

          <Form.Item
            label="Address"
            name="address"
            rules={[{ required: true, message: "Address is required" }]}
          >
            <AutoComplete
              apiKey={
                import.meta.env.VITE_GOOGLE_MAPS_API_KEY ||
                import.meta.env.VITE_APP_GOOGLE_API_URL
              }
              onPlaceSelected={onPlaceSelected}
              options={{
                types: ["establishment"],
                componentRestrictions: { country: "in" },
                fields: [
                  "address_components",
                  "formatted_address",
                  "name",
                  "geometry",
                ],
              }}
              defaultValue={address?.line1 || ""}
              style={{
                width: "100%",
                height: "36px",
                borderRadius: "8px",
                border: "1px solid #d9d9d9",
                padding: "0 12px",
              }}
            />
          </Form.Item>

          <Form.Item
            label="Area"
            name="area"
            rules={[{ required: true, message: "Area is required" }]}
          >
            <Input.TextArea placeholder="Enter area" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Country" name="country" initialValue="India">
                <Input disabled placeholder="India" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="State"
                name="state"
                rules={[{ required: true, message: "State is required" }]}
              >
                <Select
                  placeholder="Select state"
                  showSearch
                  optionFilterProp="children"
                  onChange={() => {
                    form.setFieldValue("city", undefined);
                    form.setFieldValue("pincode", undefined);
                  }}
                >
                  {allStates.map((s) => (
                    <Option key={s.isoCode} value={s.name}>
                      {s.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="City"
                name="city"
                rules={[{ required: true, message: "City is required" }]}
              >
                <Select
                  placeholder="Select city"
                  showSearch
                  optionFilterProp="children"
                  disabled={!selectedStateName}
                  onChange={() => form.setFieldValue("pincode", undefined)}
                >
                  {cities.map((c) => (
                    <Option key={c.name} value={c.name}>
                      {c.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Pincode"
                name="pincode"
                rules={[
                  { required: true, message: "Pincode is required" },
                  {
                    pattern: /^[1-9][0-9]{5}$/,
                    message: "Invalid 6-digit pincode",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  maxLength={6}
                  placeholder="Enter pincode"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item className="mt-6">
            <Button
              type="primary"
              htmlType="submit"
              className="w-full"
              size="large"
              loading={profileLoading}
            >
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default OrganisationDetail;
